import React from "react";
import { Link, Router } from "react-router-dom";
import { Alert, Button, Card, TextField, Typography, CircularProgress } from "@mui/material";

import UnauthNav from "../../components/UnauthNav/UnauthNav";
import ForgotPasswordDialog from "../../components/ForgotPasswordDialog/ForgotPasswordDialog";

import "./LoginView.scss";
import { Login } from "@mui/icons-material";
import AuthApi from "../../api/AuthApi";
import UserApi from "../../api/UserApi";
import { consumeSessionExpiredFlag } from "../../utils/sessionGuard";
const LoginView = () => {

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = React.useState(false);
  const [emailNotVerified, setEmailNotVerified] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resendMessage, setResendMessage] = React.useState<string | null>(null);
  // True when the session guard sent us here, so the redirect is explained rather than mysterious.
  const [sessionExpired] = React.useState(() => consumeSessionExpiredFlag());

  const loginReq = () => {
    setLoading(true);
    setError(false);
    setEmailNotVerified(false);
    setResendMessage(null);
    
    AuthApi.login({
      User_Email: email,
      User_Password: password
    }).then((user) => {
      console.log("Login successful", user);
      // Handle successful login, e.g., redirect to dashboard
      setTimeout(()=>{
        window.location.href = "/home";
      }, 1000)
      
    }).catch((error) => {
      console.error("Login failed", error);
      setLoading(false);
      
      if (error.code === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true);
      } else {
        setError(true);
      }
      // Handle login failure, e.g., show an error message
    });
  }

  const handleResendVerification = () => {
    if (!email) return;
    
    setResendLoading(true);
    setResendMessage(null);
    
    UserApi.resendVerification(email)
      .then((response) => {
        setResendMessage(response.message || 'Verification email sent successfully!');
        setResendLoading(false);
      })
      .catch((error) => {
        setResendMessage('Verification email sent. Please check your inbox.');
        setResendLoading(false);
        // Always show success message to prevent email enumeration
      });
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && email && password) {
      loginReq();
    }
  }

  return (
    <>
      <UnauthNav />
      <div className="linv">
        <Card className="linv-card">
          <Typography variant="h1" className="linv-card-ttl">Login</Typography>
          {sessionExpired && (
            <Alert severity="info">
              Your session has expired. Please sign in again to continue — nothing has been lost.
            </Alert>
          )}
          <TextField 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyPress}
            label="Email" 
            type="email"
            variant="outlined"
            disabled={loading}
          />
          <TextField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
            label="Password"
            variant="outlined"
            type="password"
            disabled={loading}
          />
          {error && (
            <Typography variant="body2" color="error" className="linv-card-err">Invalid email or password</Typography>
          )}
          {emailNotVerified && (
            <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
              <Typography variant="body2" color="#856404" gutterBottom>
                <strong>Email Verification Required</strong>
              </Typography>
              <Typography variant="body2" color="#856404" sx={{ mb: 1 }}>
                Please verify your email address before logging in. Check your inbox for the verification email.
              </Typography>
              <Typography variant="body2" color="#856404" sx={{ mb: 2, fontWeight: 600 }}>
                <strong>Note:</strong> Please check your spam/junk folder if you don't see the email in your inbox.
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleResendVerification}
                disabled={resendLoading || !email}
                sx={{ 
                  borderColor: '#856404', 
                  color: '#856404',
                  '&:hover': { borderColor: '#6c4e00', backgroundColor: '#fff8e1' }
                }}
              >
                {resendLoading ? <CircularProgress size={16} /> : 'Resend Verification Email'}
              </Button>
              {resendMessage && (
                <Typography variant="body2" sx={{ mt: 1, color: '#4caf50' }}>
                  {resendMessage}
                </Typography>
              )}
            </div>
          )}
          <Button 
            onClick={loginReq} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Signing In..." : "Login"}
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            disabled={loading}
            onClick={() => setForgotPasswordOpen(true)}
          >
            Forgot Password
          </Button>
          <hr />
          <Button component={Link} to="/register" variant="contained" color="inherit" className="linv-card-regbtn" disabled={loading}>Register</Button>
        </Card>
        <ForgotPasswordDialog open={forgotPasswordOpen} onClose={() => setForgotPasswordOpen(false)} />
      </div>
    </>
  );
}
export default LoginView;